import * as ExcelJS from "exceljs";
import {
  Chart,
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Filler,
  Legend,
  Tooltip,
} from "chart.js";
import {
  EVOLUTION_VALEUR_DATA,
  EvolutionDimension,
  EvolutionGranularity,
  VALEUR_PAR_FAMILLE_DATA,
  ValeurStockDetail,
} from "../../models/data.model";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Filler,
  Legend,
  Tooltip,
);

/**
 * Export Excel détaillé du widget EVOLUTION_VALEUR.
 *
 * Chaque feuille combine :
 * - **Évolution temporelle** (haut) : pivot ligne = dimension, colonnes =
 *   périodes, formats monétaires + line chart PNG embed.
 * - **Détail courant hiérarchique** (bas) : drill-down de l'état du jour
 *   alimenté par `VALEUR_PAR_FAMILLE` (details lot/stock). Famille s'ouvre
 *   sur sous-famille → article → lot ; Sous-famille sur article → lot ;
 *   Entrepôt sur article → lot ; Article sur lot. Pour Total et
 *   Propriétaire, pas de drill-down (Total = somme globale ; Propriétaire
 *   n'est pas porté par les details ValeurStockDetail aujourd'hui).
 */

const PALETTE = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
];

const C = {
  // Palette unifiée avec l'export VALEUR_PAR_FAMILLE pour cohérence
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3",
  groupFont: "FF1B2A4A",
  subGroupBg: "FFEEF2F8",
  articleBg: "FFF5F7FA",
  border: "FFD0D5DD",
  lotFont: "FF667085",
  lotBorder: "FFE4E7EC",
  positiveBg: "FFE6FFF5",
  positiveFont: "FF1F6F4F",
  negativeBg: "FFFFE6E6",
  negativeFont: "FFA0291D",
  kpiBg: "FFEEF4FF",
  kpiAccent: "FF7C3AED",
  titleColor: "FF1B2A4A",
  mutedColor: "FF667085",
};

const GRANULARITY_LABEL: Record<EvolutionGranularity, string> = {
  day: "Jour",
  week: "Semaine",
  month: "Mois",
  quarter: "Trimestre",
  year: "Année",
};

const DIMENSION_LABEL: Record<EvolutionDimension, string> = {
  total: "Total",
  famille: "Famille",
  sous_famille: "Sous-famille",
  article: "Article",
  entrepot: "Entrepôt",
  owner: "Propriétaire",
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c--;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

function fmtDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

interface PivotInput {
  dates: string[];
  rows: { label: string; valuesByDate: Map<string, number> }[];
}

function buildPivot(
  series: EVOLUTION_VALEUR_DATA["series"],
): PivotInput {
  const dateSet = new Set<string>();
  for (const s of series) for (const p of s.points) dateSet.add(p.date);
  const dates = Array.from(dateSet).sort();
  const rows = series.map((s) => {
    const map = new Map<string, number>();
    for (const p of s.points) map.set(p.date, p.total);
    return { label: s.dimensionLabel, valuesByDate: map };
  });
  rows.sort((a, b) => {
    const av = a.valuesByDate.get(dates[dates.length - 1]) ?? 0;
    const bv = b.valuesByDate.get(dates[dates.length - 1]) ?? 0;
    return bv - av;
  });
  return { dates, rows };
}

// ─────────────────────────────────────────────────────────────────────────────
// Cell styles — repris du pattern valeur-stock-excel pour cohérence visuelle
// ─────────────────────────────────────────────────────────────────────────────

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, color: { argb: C.headerFont }, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { vertical: "middle", horizontal: "left" };
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
  };
}

function groupStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.groupFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
  };
}

function subGroupStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 9.5, color: { argb: C.groupFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.subGroupBg } };
  cell.border = { bottom: { style: "thin", color: { argb: C.border } } };
}

function articleStyle(cell: ExcelJS.Cell) {
  cell.font = { size: 9, italic: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.articleBg } };
  cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
}

function lotStyle(cell: ExcelJS.Cell) {
  cell.font = { size: 9, color: { argb: C.lotFont } };
  cell.border = { bottom: { style: "hair", color: { argb: C.lotBorder } } };
}

// ─────────────────────────────────────────────────────────────────────────────
// Pivot temporel (haut de chaque feuille dimension)
// ─────────────────────────────────────────────────────────────────────────────

async function writeEvolutionPivot(
  wb: ExcelJS.Workbook,
  sheet: ExcelJS.Worksheet,
  title: string,
  pivot: PivotInput,
  granularity: EvolutionGranularity,
  startRow: number,
): Promise<{ endRow: number; pivotChartRow: number }> {
  // Titre de section
  sheet.getCell(startRow, 1).value = title;
  sheet.getCell(startRow, 1).font = {
    bold: true, size: 13, color: { argb: C.titleColor },
  };
  sheet.mergeCells(startRow, 1, startRow, Math.max(2, pivot.dates.length + 2));

  // Sous-titre
  const subRow = startRow + 1;
  sheet.getCell(subRow, 1).value = `Granularité : ${GRANULARITY_LABEL[granularity]} • ${pivot.dates.length} période(s)`;
  sheet.getCell(subRow, 1).font = { italic: true, color: { argb: C.mutedColor }, size: 10 };
  sheet.mergeCells(subRow, 1, subRow, Math.max(2, pivot.dates.length + 2));

  // Headers
  const headerRow = startRow + 3;
  sheet.getCell(headerRow, 1).value = "Dimension";
  for (let i = 0; i < pivot.dates.length; i++) {
    sheet.getCell(headerRow, 2 + i).value = fmtDate(pivot.dates[i]);
  }
  const deltaCol = 2 + pivot.dates.length;
  sheet.getCell(headerRow, deltaCol).value = "Variation";
  for (let c = 1; c <= deltaCol; c++) {
    headerStyle(sheet.getCell(headerRow, c));
    sheet.getCell(headerRow, c).alignment = {
      vertical: "middle",
      horizontal: c === 1 ? "left" : "right",
    };
  }

  // Lignes
  const dataStartRow = headerRow + 1;
  pivot.rows.forEach((r, idx) => {
    const row = dataStartRow + idx;
    sheet.getCell(row, 1).value = r.label;
    let lastVal: number | null = null;
    let firstVal: number | null = null;
    for (let i = 0; i < pivot.dates.length; i++) {
      const v = r.valuesByDate.get(pivot.dates[i]);
      if (v != null) lastVal = v;
      if (firstVal == null && lastVal != null) firstVal = lastVal;
      const cell = sheet.getCell(row, 2 + i);
      if (lastVal != null) {
        cell.value = Math.round(lastVal * 100) / 100;
        cell.numFmt = '#,##0.00\\ "€"';
      }
      cell.alignment = { horizontal: "right" };
    }
    if (lastVal != null && firstVal != null) {
      const delta = lastVal - firstVal;
      const cell = sheet.getCell(row, deltaCol);
      cell.value = Math.round(delta * 100) / 100;
      cell.numFmt = '+#,##0.00\\ "€";-#,##0.00\\ "€";0';
      cell.alignment = { horizontal: "right" };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: delta >= 0 ? C.positiveBg : C.negativeBg },
      };
      cell.font = {
        bold: true,
        color: { argb: delta >= 0 ? C.positiveFont : C.negativeFont },
      };
    }
  });

  // Ligne TOTAL en bas
  const totalRow = dataStartRow + pivot.rows.length;
  if (pivot.rows.length > 1) {
    sheet.getCell(totalRow, 1).value = "TOTAL";
    groupStyle(sheet.getCell(totalRow, 1));
    for (let i = 0; i < pivot.dates.length; i++) {
      const c = sheet.getCell(totalRow, 2 + i);
      const startLetter = colLetter(2 + i);
      c.value = {
        formula: `SUM(${startLetter}${dataStartRow}:${startLetter}${dataStartRow + pivot.rows.length - 1})`,
      };
      c.numFmt = '#,##0.00\\ "€"';
      c.font = { bold: true };
      c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
      c.alignment = { horizontal: "right" };
    }
    const dCell = sheet.getCell(totalRow, deltaCol);
    dCell.value = {
      formula: `${colLetter(deltaCol)}${dataStartRow + pivot.rows.length - 1}`,
    };
    dCell.font = { bold: true };
    dCell.alignment = { horizontal: "right" };
    dCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
  }

  // Largeurs colonnes
  sheet.getColumn(1).width = 36;
  for (let i = 0; i < pivot.dates.length; i++) sheet.getColumn(2 + i).width = 14;
  sheet.getColumn(deltaCol).width = 18;

  return { endRow: totalRow, pivotChartRow: totalRow + 2 };
}

function renderLineChartPng(pivot: PivotInput, title: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = 900;
  canvas.height = 360;
  const ctx = canvas.getContext("2d")!;

  const top = pivot.rows.slice(0, 7);
  const datasets = top.map((r, i) => {
    let lastVal: number | null = null;
    return {
      label: r.label,
      data: pivot.dates.map((d) => {
        const v = r.valuesByDate.get(d);
        if (v != null) lastVal = v;
        return lastVal;
      }),
      borderColor: PALETTE[i % PALETTE.length],
      backgroundColor: PALETTE[i % PALETTE.length] + "22",
      borderWidth: 2,
      pointRadius: 0,
      tension: 0.2,
      fill: false,
      spanGaps: true,
    };
  });

  const chart = new Chart(ctx, {
    type: "line",
    data: { labels: pivot.dates.map(fmtDate), datasets },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: true, position: "bottom", labels: { font: { size: 10 } } },
        title: { display: true, text: title, font: { size: 14, weight: "bold" } },
      },
      scales: {
        x: { ticks: { autoSkip: true, maxTicksLimit: 12, font: { size: 9 } } },
        y: {
          ticks: {
            font: { size: 9 },
            callback: (v) => `${(Number(v) / 1000).toFixed(0)} k€`,
          },
        },
      },
    },
  });

  const dataUrl = canvas.toDataURL("image/png");
  chart.destroy();
  return dataUrl.replace(/^data:image\/png;base64,/, "");
}

// ─────────────────────────────────────────────────────────────────────────────
// Drill-down hiérarchique (bas de chaque feuille dimension)
//
// Reprend la structure du widget VALEUR_PAR_FAMILLE existant : un tableau à
// 9 colonnes commun à tous les niveaux (Groupe / Article / N° Lot / DLC /
// Entrepôt / Quantité / Unité / Coût unit / Total) avec des lignes à
// indentation croissante (groupe → sous-groupe → article → lot).
// ─────────────────────────────────────────────────────────────────────────────

const DRILL_HEADERS = [
  "Groupe", "Article", "N° Lot", "DDM/DLC", "Entrepôt",
  "Quantité", "Unité", "Coût unit. (€)", "Total (€)",
];
const DRILL_WIDTHS = [28, 32, 16, 14, 22, 12, 8, 14, 16];

type DrillMode = "famille" | "sous_famille" | "entrepot" | "article";

function writeDrillDown(
  sheet: ExcelJS.Worksheet,
  details: ValeurStockDetail[],
  mode: DrillMode,
  startRow: number,
): number {
  // Titre de section.
  const drillTree =
    mode === "famille" ? "Famille → Sous-famille → Article → Lot" :
    mode === "sous_famille" ? "Sous-famille → Article → Lot" :
    mode === "entrepot" ? "Entrepôt → Article → Lot" :
    "Article → Lot";
  const sectionTitle = `Détail du stock — ${drillTree}`;

  sheet.getCell(startRow, 1).value = sectionTitle;
  sheet.getCell(startRow, 1).font = {
    bold: true, size: 13, color: { argb: C.titleColor },
  };
  sheet.mergeCells(startRow, 1, startRow, 9);

  // Bandeau d'avertissement BIEN VISIBLE — la date est en gros et l'écart
  // potentiel avec l'historique du haut est expliqué noir sur jaune. Sans
  // ce bandeau, l'utilisateur peut croire à une incohérence (totaux du
  // tableau du haut ≠ totaux du détail) alors qu'il s'agit de 2 dates
  // différentes (snapshot historique vs état du jour).
  const todayLabel = new Date().toLocaleDateString("fr-FR");
  const bannerRow = startRow + 1;
  const bannerCell = sheet.getCell(bannerRow, 1);
  bannerCell.value = {
    richText: [
      {
        text: "⚠  ÉTAT DU STOCK AU ",
        font: { bold: true, size: 12, color: { argb: "FF7C2D12" } },
      },
      {
        text: todayLabel,
        font: { bold: true, size: 14, color: { argb: "FF7C2D12" } },
      },
      {
        text:
          "  —  Si vous avez choisi une plage passée plus haut, " +
          "les totaux ci-dessous peuvent différer (le stock a évolué " +
          "depuis : consommations, réceptions, mouvements).",
        font: { size: 11, color: { argb: "FF7C2D12" }, italic: true },
      },
    ],
  } as any;
  bannerCell.alignment = { wrapText: true, vertical: "middle", horizontal: "left" };
  bannerCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFEF3C7" }, // jaune ambre pâle (Tailwind amber-100)
  };
  bannerCell.border = {
    top: { style: "medium", color: { argb: "FFD97706" } },
    bottom: { style: "medium", color: { argb: "FFD97706" } },
    left: { style: "medium", color: { argb: "FFD97706" } },
    right: { style: "medium", color: { argb: "FFD97706" } },
  };
  sheet.mergeCells(bannerRow, 1, bannerRow, 9);
  sheet.getRow(bannerRow).height = 44;

  // Headers — décalés d'une ligne pour faire place au bandeau.
  const headerRow = startRow + 3;
  DRILL_HEADERS.forEach((h, i) => {
    const cell = sheet.getCell(headerRow, i + 1);
    cell.value = h;
    headerStyle(cell);
    cell.alignment = { vertical: "middle", horizontal: i >= 5 ? "right" : "left" };
  });

  let row = headerRow + 1;

  /** Écrit les lots (niveau le plus bas) sous un article. */
  const writeLots = (lots: ValeurStockDetail[]) => {
    const sortedLots = [...lots].sort((a, b) => b.total - a.total);
    for (const lot of sortedLots) {
      const r = sheet.getRow(row);
      r.getCell(3).value = lot.lotNumero;
      r.getCell(4).value = lot.dluo ?? "";
      r.getCell(5).value = lot.entrepot;
      r.getCell(6).value = lot.quantite;
      r.getCell(7).value = lot.unite;
      r.getCell(8).value = lot.coutUnitaire;
      r.getCell(9).value = lot.total;
      for (let c = 1; c <= 9; c++) lotStyle(r.getCell(c));
      r.getCell(6).numFmt = "#,##0.00";
      r.getCell(8).numFmt = '#,##0.00\\ "€"';
      r.getCell(9).numFmt = '#,##0.00\\ "€"';
      r.getCell(6).alignment = { horizontal: "right" };
      r.getCell(8).alignment = { horizontal: "right" };
      r.getCell(9).alignment = { horizontal: "right" };
      row++;
    }
  };

  /** Écrit un article + ses lots. Retourne le total agrégé. */
  const writeArticle = (artName: string, lots: ValeurStockDetail[]): number => {
    const total = lots.reduce((s, l) => s + l.total, 0);
    const qty = lots.reduce((s, l) => s + l.quantite, 0);
    const r = sheet.getRow(row);
    r.getCell(2).value = artName;
    r.getCell(6).value = qty;
    r.getCell(9).value = total;
    for (let c = 1; c <= 9; c++) articleStyle(r.getCell(c));
    r.getCell(6).numFmt = "#,##0.00";
    r.getCell(9).numFmt = '#,##0.00\\ "€"';
    r.getCell(6).alignment = { horizontal: "right" };
    r.getCell(9).alignment = { horizontal: "right" };
    row++;
    writeLots(lots);
    return total;
  };

  /** Hiérarchie complète selon le mode. */
  if (mode === "famille") {
    // Famille → Sous-famille → Article → Lot
    const fams = new Map<string, Map<string | null, Map<string, ValeurStockDetail[]>>>();
    for (const d of details) {
      if (d.total <= 0) continue;
      if (!fams.has(d.famille)) fams.set(d.famille, new Map());
      const sfMap = fams.get(d.famille)!;
      const sfKey = d.sousFamille ?? null;
      if (!sfMap.has(sfKey)) sfMap.set(sfKey, new Map());
      const artMap = sfMap.get(sfKey)!;
      if (!artMap.has(d.article)) artMap.set(d.article, []);
      artMap.get(d.article)!.push(d);
    }

    const sortedFams = [...fams.entries()]
      .map(([fam, sfMap]) => ({
        fam,
        sfMap,
        total: [...sfMap.values()].flatMap((m) =>
          [...m.values()].map((lots) => lots.reduce((s, l) => s + l.total, 0)),
        ).reduce((s, v) => s + v, 0),
      }))
      .sort((a, b) => b.total - a.total);

    for (const { fam, sfMap, total: famTotal } of sortedFams) {
      const famRow = sheet.getRow(row);
      famRow.getCell(1).value = fam;
      famRow.getCell(9).value = famTotal;
      for (let c = 1; c <= 9; c++) groupStyle(famRow.getCell(c));
      famRow.getCell(9).numFmt = '#,##0.00\\ "€"';
      famRow.getCell(9).alignment = { horizontal: "right" };
      row++;

      // Sous-familles : on itère, en mettant les orphelins (sf=null)
      // directement comme articles sous la famille (pas de wrapper)
      const orderedSf = [...sfMap.entries()].sort(([, a], [, b]) => {
        const at = [...a.values()].flat().reduce((s, l) => s + l.total, 0);
        const bt = [...b.values()].flat().reduce((s, l) => s + l.total, 0);
        return bt - at;
      });
      for (const [sfKey, artMap] of orderedSf) {
        const orderedArts = [...artMap.entries()].sort(([, a], [, b]) => {
          const at = a.reduce((s, l) => s + l.total, 0);
          const bt = b.reduce((s, l) => s + l.total, 0);
          return bt - at;
        });

        if (sfKey == null) {
          // articles orphelins — directement sous la famille
          for (const [art, lots] of orderedArts) writeArticle(art, lots);
        } else {
          const sfTotal = [...artMap.values()].flat().reduce((s, l) => s + l.total, 0);
          const sfRow = sheet.getRow(row);
          sfRow.getCell(1).value = `  ↳ ${sfKey}`;
          sfRow.getCell(9).value = sfTotal;
          for (let c = 1; c <= 9; c++) subGroupStyle(sfRow.getCell(c));
          sfRow.getCell(9).numFmt = '#,##0.00\\ "€"';
          sfRow.getCell(9).alignment = { horizontal: "right" };
          row++;
          for (const [art, lots] of orderedArts) writeArticle(art, lots);
        }
      }
    }
  } else if (mode === "sous_famille") {
    // Sous-famille → Article → Lot
    const sfs = new Map<string, Map<string, ValeurStockDetail[]>>();
    for (const d of details) {
      if (d.total <= 0) continue;
      const sfKey = d.sousFamille ?? "Non classé";
      if (!sfs.has(sfKey)) sfs.set(sfKey, new Map());
      const artMap = sfs.get(sfKey)!;
      if (!artMap.has(d.article)) artMap.set(d.article, []);
      artMap.get(d.article)!.push(d);
    }
    const sorted = [...sfs.entries()]
      .map(([sf, m]) => ({
        sf, m,
        total: [...m.values()].flat().reduce((s, l) => s + l.total, 0),
      }))
      .sort((a, b) => b.total - a.total);
    for (const { sf, m, total } of sorted) {
      const sfRow = sheet.getRow(row);
      sfRow.getCell(1).value = sf;
      sfRow.getCell(9).value = total;
      for (let c = 1; c <= 9; c++) groupStyle(sfRow.getCell(c));
      sfRow.getCell(9).numFmt = '#,##0.00\\ "€"';
      sfRow.getCell(9).alignment = { horizontal: "right" };
      row++;
      const orderedArts = [...m.entries()].sort(([, a], [, b]) => {
        return b.reduce((s, l) => s + l.total, 0) - a.reduce((s, l) => s + l.total, 0);
      });
      for (const [art, lots] of orderedArts) writeArticle(art, lots);
    }
  } else if (mode === "entrepot") {
    // Entrepôt → Article → Lot
    const ents = new Map<string, Map<string, ValeurStockDetail[]>>();
    for (const d of details) {
      if (d.total <= 0) continue;
      if (!ents.has(d.entrepot)) ents.set(d.entrepot, new Map());
      const artMap = ents.get(d.entrepot)!;
      if (!artMap.has(d.article)) artMap.set(d.article, []);
      artMap.get(d.article)!.push(d);
    }
    const sorted = [...ents.entries()]
      .map(([ent, m]) => ({
        ent, m,
        total: [...m.values()].flat().reduce((s, l) => s + l.total, 0),
      }))
      .sort((a, b) => b.total - a.total);
    for (const { ent, m, total } of sorted) {
      const entRow = sheet.getRow(row);
      entRow.getCell(1).value = ent;
      entRow.getCell(9).value = total;
      for (let c = 1; c <= 9; c++) groupStyle(entRow.getCell(c));
      entRow.getCell(9).numFmt = '#,##0.00\\ "€"';
      entRow.getCell(9).alignment = { horizontal: "right" };
      row++;
      const orderedArts = [...m.entries()].sort(([, a], [, b]) => {
        return b.reduce((s, l) => s + l.total, 0) - a.reduce((s, l) => s + l.total, 0);
      });
      for (const [art, lots] of orderedArts) writeArticle(art, lots);
    }
  } else if (mode === "article") {
    // Article → Lot uniquement
    const arts = new Map<string, ValeurStockDetail[]>();
    for (const d of details) {
      if (d.total <= 0) continue;
      if (!arts.has(d.article)) arts.set(d.article, []);
      arts.get(d.article)!.push(d);
    }
    const sorted = [...arts.entries()]
      .map(([art, lots]) => ({
        art, lots,
        total: lots.reduce((s, l) => s + l.total, 0),
      }))
      .sort((a, b) => b.total - a.total);
    for (const { art, lots } of sorted) {
      const total = lots.reduce((s, l) => s + l.total, 0);
      const qty = lots.reduce((s, l) => s + l.quantite, 0);
      const artRow = sheet.getRow(row);
      artRow.getCell(1).value = art;
      artRow.getCell(6).value = qty;
      artRow.getCell(9).value = total;
      for (let c = 1; c <= 9; c++) groupStyle(artRow.getCell(c));
      artRow.getCell(6).numFmt = "#,##0.00";
      artRow.getCell(9).numFmt = '#,##0.00\\ "€"';
      artRow.getCell(6).alignment = { horizontal: "right" };
      artRow.getCell(9).alignment = { horizontal: "right" };
      row++;
      writeLots(lots);
    }
  }

  return row;
}

// ─────────────────────────────────────────────────────────────────────────────
// Synthèse — KPI cards + top dimensions
// ─────────────────────────────────────────────────────────────────────────────

async function writeSyntheseSheet(
  wb: ExcelJS.Workbook,
  payloads: Record<EvolutionDimension, EVOLUTION_VALEUR_DATA | null>,
  granularity: EvolutionGranularity,
  range: { startDate: string; endDate: string },
) {
  const sheet = wb.addWorksheet("Synthèse");
  await addJustupBranding(wb, sheet, 9);

  // Largeurs cols pour layout 4 KPI cards (cols 1-2, 3-4, 5-6, 7-8) + branding 9
  for (let c = 1; c <= 8; c++) sheet.getColumn(c).width = 16;
  sheet.getColumn(9).width = 14;

  // ── Titre principal ──
  sheet.mergeCells("A2:H2");
  const titleCell = sheet.getCell("A2");
  titleCell.value = "Évolution valeur stock";
  titleCell.font = { bold: true, size: 22, color: { argb: C.titleColor } };
  titleCell.alignment = { vertical: "middle", horizontal: "center" };
  sheet.getRow(2).height = 36;

  sheet.mergeCells("A3:H3");
  const subCell = sheet.getCell("A3");
  subCell.value =
    `Période : ${fmtDate(range.startDate)} → ${fmtDate(range.endDate)}` +
    `  •  Granularité : ${GRANULARITY_LABEL[granularity]}` +
    `  •  Export : ${new Date().toLocaleString("fr-FR")}`;
  subCell.font = { italic: true, color: { argb: C.mutedColor }, size: 11 };
  subCell.alignment = { vertical: "middle", horizontal: "center" };
  sheet.getRow(3).height = 22;

  // ── 4 KPI cards (lignes 5-8) ──
  // Chaque card occupe 2 colonnes : titre dans la 1re, valeur en bas merge.
  const total = payloads.total;
  const kpis = total
    ? [
        {
          label: "VALEUR FIN DE PÉRIODE",
          value: total.delta.endValue,
          fmt: "money",
          subtitle: `au ${fmtDate(range.endDate)}`,
        },
        {
          label: "VALEUR DÉBUT DE PÉRIODE",
          value: total.delta.startValue,
          fmt: "money",
          subtitle: `au ${fmtDate(range.startDate)}`,
        },
        {
          label: "VARIATION ABSOLUE",
          value: total.delta.absolute,
          fmt: "moneySigned",
          subtitle: total.delta.absolute >= 0 ? "↗ en hausse" : "↘ en baisse",
        },
        {
          label: "VARIATION RELATIVE",
          value: total.delta.percent,
          fmt: "percentSigned",
          subtitle: "vs début de période",
        },
      ]
    : [];

  const KPI_TOP = 5;
  for (let i = 0; i < kpis.length; i++) {
    const k = kpis[i];
    const colStart = 1 + i * 2;
    const colEnd = colStart + 1;

    // Ligne label
    sheet.mergeCells(KPI_TOP, colStart, KPI_TOP, colEnd);
    const lbl = sheet.getCell(KPI_TOP, colStart);
    lbl.value = k.label;
    lbl.font = { bold: true, size: 9, color: { argb: C.mutedColor } };
    lbl.alignment = { vertical: "middle", horizontal: "center" };
    lbl.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.kpiBg } };
    lbl.border = {
      top: { style: "medium", color: { argb: C.kpiAccent } },
      left: { style: "thin", color: { argb: C.border } },
      right: { style: "thin", color: { argb: C.border } },
    };

    // Ligne valeur
    sheet.mergeCells(KPI_TOP + 1, colStart, KPI_TOP + 1, colEnd);
    const val = sheet.getCell(KPI_TOP + 1, colStart);
    if (k.fmt === "money") {
      val.value = k.value;
      val.numFmt = '#,##0.00\\ "€"';
    } else if (k.fmt === "moneySigned") {
      val.value = k.value;
      val.numFmt = '+#,##0.00\\ "€";-#,##0.00\\ "€";0';
    } else if (k.fmt === "percentSigned") {
      val.value = k.value == null ? "—" : k.value / 100;
      if (k.value != null) val.numFmt = "+0.00%;-0.00%;0.00%";
    }
    const isVariation = k.fmt === "moneySigned" || k.fmt === "percentSigned";
    const positive = typeof k.value === "number" && k.value >= 0;
    val.font = {
      bold: true,
      size: 18,
      color: {
        argb: isVariation
          ? positive
            ? C.positiveFont
            : C.negativeFont
          : C.titleColor,
      },
    };
    val.alignment = { vertical: "middle", horizontal: "center" };
    val.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.kpiBg } };
    val.border = {
      left: { style: "thin", color: { argb: C.border } },
      right: { style: "thin", color: { argb: C.border } },
    };
    sheet.getRow(KPI_TOP + 1).height = 32;

    // Ligne sous-titre
    sheet.mergeCells(KPI_TOP + 2, colStart, KPI_TOP + 2, colEnd);
    const sub = sheet.getCell(KPI_TOP + 2, colStart);
    sub.value = k.subtitle;
    sub.font = { italic: true, size: 9, color: { argb: C.mutedColor } };
    sub.alignment = { vertical: "middle", horizontal: "center" };
    sub.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.kpiBg } };
    sub.border = {
      bottom: { style: "thin", color: { argb: C.border } },
      left: { style: "thin", color: { argb: C.border } },
      right: { style: "thin", color: { argb: C.border } },
    };
  }

  // ── Top 5 par dimension (lignes 10+) ──
  const topRowStart = KPI_TOP + 5;
  const topDims: Array<{ key: EvolutionDimension; label: string; col: number }> = [
    { key: "famille", label: "Top 5 familles", col: 1 },
    { key: "entrepot", label: "Top 5 entrepôts", col: 4 },
    { key: "article", label: "Top 5 articles", col: 7 },
  ];

  topDims.forEach(({ key, label, col }) => {
    const data = payloads[key];
    if (!data) return;

    sheet.mergeCells(topRowStart, col, topRowStart, col + 1);
    const titleC = sheet.getCell(topRowStart, col);
    titleC.value = label;
    titleC.font = { bold: true, size: 11, color: { argb: C.headerFont } };
    titleC.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
    titleC.alignment = { horizontal: "left", vertical: "middle" };

    // Valeur "actuelle" = valeur à la date la plus récente de la fenêtre, et
    // NON le dernier point de chaque série : une dimension vidée en cours de
    // période (sans point à la date de fin) doit valoir 0, pas sa dernière
    // valeur figée — sinon la somme dépasse le total réel.
    const refDate = data.series.reduce<string | null>((max, s) => {
      const last = s.points[s.points.length - 1]?.date;
      return last && (max === null || last > max) ? last : max;
    }, null);
    const valueAtRef = (s: { points: { date: string; total: number }[] }) =>
      refDate === null
        ? s.points[s.points.length - 1]?.total ?? 0
        : s.points.find((p) => p.date === refDate)?.total ?? 0;
    const sorted = [...data.series]
      .map((s) => ({ label: s.dimensionLabel, v: valueAtRef(s) }))
      .sort((a, b) => b.v - a.v)
      .slice(0, 5);

    sorted.forEach((r, idx) => {
      const rowN = topRowStart + 1 + idx;
      const lblC = sheet.getCell(rowN, col);
      lblC.value = r.label;
      lblC.font = { size: 10 };
      lblC.alignment = { horizontal: "left" };
      const valC = sheet.getCell(rowN, col + 1);
      valC.value = Math.round(r.v * 100) / 100;
      valC.numFmt = '#,##0\\ "€"';
      valC.font = { size: 10, bold: true };
      valC.alignment = { horizontal: "right" };
      // Bordure légère entre lignes
      lblC.border = valC.border = {
        bottom: { style: "hair", color: { argb: C.border } },
      };
    });
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Données brutes
// ─────────────────────────────────────────────────────────────────────────────

function writeRawSheet(
  wb: ExcelJS.Workbook,
  payloads: Record<EvolutionDimension, EVOLUTION_VALEUR_DATA | null>,
) {
  const sheet = wb.addWorksheet("Données brutes", {
    views: [{ state: "frozen", ySplit: 1 }],
  });
  const headers = ["Date", "Dimension", "Clé", "Valeur (€)", "Quantité"];
  headers.forEach((h, i) => {
    const c = sheet.getCell(1, i + 1);
    c.value = h;
    headerStyle(c);
  });
  let row = 2;
  for (const [dim, data] of Object.entries(payloads)) {
    if (!data) continue;
    for (const s of data.series) {
      for (const p of s.points) {
        sheet.getCell(row, 1).value = p.date;
        sheet.getCell(row, 2).value = DIMENSION_LABEL[dim as EvolutionDimension];
        sheet.getCell(row, 3).value = s.dimensionLabel;
        sheet.getCell(row, 4).value = Math.round(p.total * 100) / 100;
        sheet.getCell(row, 4).numFmt = '#,##0.00\\ "€"';
        sheet.getCell(row, 5).value = Math.round(p.quantite * 1000) / 1000;
        sheet.getCell(row, 5).numFmt = "#,##0.000";
        row++;
      }
    }
  }
  sheet.getColumn(1).width = 12;
  sheet.getColumn(2).width = 16;
  sheet.getColumn(3).width = 36;
  sheet.getColumn(4).width = 18;
  sheet.getColumn(5).width = 16;
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: 5 } };
}

// ─────────────────────────────────────────────────────────────────────────────
// Construction d'une feuille dimension complète
// (Évolution temporelle + chart + drill-down hiérarchique optionnel)
// ─────────────────────────────────────────────────────────────────────────────

async function buildDimensionSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  pivotTitle: string,
  data: EVOLUTION_VALEUR_DATA,
  granularity: EvolutionGranularity,
  drill: { mode: DrillMode; details: ValeurStockDetail[] } | null,
) {
  const sheet = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", xSplit: 1, ySplit: 5 }],
  });

  // Largeurs colonnes pour drill-down (9 colonnes) — couvrent aussi le pivot
  DRILL_WIDTHS.forEach((w, i) => (sheet.getColumn(i + 1).width = w));

  await addJustupBranding(wb, sheet, Math.max(9, data.total.length + 2));

  const pivot = buildPivot(data.series);
  const { endRow, pivotChartRow } = await writeEvolutionPivot(
    wb, sheet, pivotTitle, pivot, granularity, 2,
  );

  // Embed chart au-dessous du pivot
  try {
    if (pivot.dates.length > 0 && pivot.rows.length > 0) {
      const png = renderLineChartPng(pivot, pivotTitle);
      const imgId = wb.addImage({ base64: png, extension: "png" });
      sheet.addImage(imgId, {
        tl: { col: 0, row: pivotChartRow - 1 },
        ext: { width: 900, height: 360 },
      });
    }
  } catch (e) {
    console.warn("Chart embed failed for", sheetName, e);
  }

  // Drill-down hiérarchique (optionnel — Total/Owner n'en ont pas)
  if (drill) {
    const drillStartRow = pivotChartRow + 22; // espace sous le chart embed
    writeDrillDown(sheet, drill.details, drill.mode, drillStartRow);
  }

  return sheet;
}

// ─────────────────────────────────────────────────────────────────────────────
// Export principal
// ─────────────────────────────────────────────────────────────────────────────

export async function exportEvolutionValeurExcel(payloads: {
  total: EVOLUTION_VALEUR_DATA | null;
  famille: EVOLUTION_VALEUR_DATA | null;
  sous_famille: EVOLUTION_VALEUR_DATA | null;
  article: EVOLUTION_VALEUR_DATA | null;
  entrepot: EVOLUTION_VALEUR_DATA | null;
  owner: EVOLUTION_VALEUR_DATA | null;
  /** Snapshot courant complet (avec details) — alimente les drill-downs. */
  current: VALEUR_PAR_FAMILLE_DATA | null;
  granularity: EvolutionGranularity;
  startDate: string;
  endDate: string;
}) {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, {
    title: "Évolution valeur stock",
    subject: `Évolution valeur stock — ${payloads.startDate} → ${payloads.endDate}`,
  });

  // 1. Synthèse
  await writeSyntheseSheet(
    wb,
    {
      total: payloads.total,
      famille: payloads.famille,
      sous_famille: payloads.sous_famille,
      article: payloads.article,
      entrepot: payloads.entrepot,
      owner: payloads.owner,
    },
    payloads.granularity,
    { startDate: payloads.startDate, endDate: payloads.endDate },
  );

  const details = payloads.current?.details ?? [];

  // 2-7. Une feuille par dimension
  type DimSpec = {
    key: EvolutionDimension;
    sheetName: string;
    title: string;
    drill: DrillMode | null;
  };
  const dims: DimSpec[] = [
    { key: "total", sheetName: "Total", title: "Évolution valeur stock — Total", drill: null },
    { key: "famille", sheetName: "Famille", title: "Évolution par famille", drill: "famille" },
    { key: "sous_famille", sheetName: "Sous-famille", title: "Évolution par sous-famille", drill: "sous_famille" },
    { key: "article", sheetName: "Article", title: "Évolution par article", drill: "article" },
    { key: "entrepot", sheetName: "Entrepôt", title: "Évolution par entrepôt", drill: "entrepot" },
    { key: "owner", sheetName: "Propriétaire", title: "Évolution par propriétaire", drill: null },
  ];

  for (const dim of dims) {
    const data = payloads[dim.key];
    if (!data || !data.series.length) continue;
    await buildDimensionSheet(
      wb, dim.sheetName, dim.title, data, payloads.granularity,
      dim.drill && details.length ? { mode: dim.drill, details } : null,
    );
  }

  // 8. Données brutes
  writeRawSheet(wb, {
    total: payloads.total,
    famille: payloads.famille,
    sous_famille: payloads.sous_famille,
    article: payloads.article,
    entrepot: payloads.entrepot,
    owner: payloads.owner,
  });

  // Téléchargement
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `evolution-valeur-stock-${payloads.startDate}_${payloads.endDate}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
